import { useEffect } from 'react';
import { Stack, FormHelperText, Divider, Grid, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import type { FormikProps } from 'formik';

import { type TestProps, Test as TestModel, isLocationOnsite } from '../../../models';
import { TestSelectionSummary, TestSelector, TestTotalSummary } from '../../../components/bulk-request';
import { TestSelectionSkeleton } from '../../../components/shared';
import { useGetBulkTestsQuery } from '../../../slices/apis/testApi';
import { useGetSuppliersQuery } from '../../../slices/apis/testingCentreApi';
import { useGetBulkTestDiscountRuleQuery } from '../../../slices/apis/bulkRequestApi';
import { resolveDiscountedUnitPrice } from '../../../utils/pricingCalculator';

interface BulkRequestTestPickerProps {
	formik: FormikProps<{ tests: TestProps[] }>;
	allowQtyEditing: boolean;
}

export const BulkRequestTest: React.FC<BulkRequestTestPickerProps> = ({ formik, allowQtyEditing }) => {
	const { data } = useGetBulkTestsQuery();
	const { data: testingCentres } = useGetSuppliersQuery();
	const { data: discountRules } = useGetBulkTestDiscountRuleQuery();
	const { pathname } = useLocation();

	const handleChange = (testId: TestProps['id'], value: number | null = 0) => {
		const quantity = value ?? 0;

		const normalizedTests = formik.values.tests.map((test) => {
			if (Number(test.id) !== Number(testId)) {
				return test;
			}

			return {
				...test,
				quantity,
			};
		});

		const repricedTests = normalizedTests.map((test) => {
			const discountedPrice = resolveDiscountedUnitPrice(test, normalizedTests, discountRules);

			if (discountedPrice === undefined) {
				const { discountedPrice: _, ...rest } = test;
				return rest;
			}

			return {
				...test,
				discountedPrice,
			};
		});

		formik.setFieldValue('tests', repricedTests);
	};

	const ifError = Boolean(formik.touched.tests) && Boolean(formik.errors.tests);

	// if new and onsite then, find travel fee and make it qty 1 and set min 1
	// else read formik.values.tests as it's edit
	// @ts-ignore
	// biome-ignore lint/correctness/useExhaustiveDependencies: include formik causes crash for unknown reason
	useEffect(() => {
		if (data && testingCentres && pathname.includes('new')) {
			// @ts-ignore
			const onsiteLocation = isLocationOnsite(testingCentres, formik.values.location);
			if (formik.values.tests.length === 0) {
				formik.setFieldValue('tests', TestModel.normaliseBulkTest(data, onsiteLocation));
			} else {
				formik.setFieldValue('tests', TestModel.normaliseBulkTest(formik.values.tests, onsiteLocation));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, testingCentres, pathname]);

	if (!data) {
		return <TestSelectionSkeleton />;
	}

	return (
		<>
			{ifError && (
				<FormHelperText error sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
					{formik.errors.tests as string}
				</FormHelperText>
			)}
			<Stack spacing={1}>
				<Grid container spacing={2} direction="row">
					{formik.values.tests.map((test) => {
						return (
							<Grid key={test.name}>
								<TestSelector
									test={test}
									key={test.name}
									onQtyChange={(val, event) => {
										allowQtyEditing && handleChange(test.id, val);
									}}
								/>
							</Grid>
						);
					})}
				</Grid>
				<Typography variant="h5">Summary</Typography>
				<Divider sx={{ divider: { borderBottomWidth: 1.5 }, my: 0.75 }} />
				<Stack spacing={2}>
					<TestSelectionSummary tests={TestModel.removeNilTest(formik.values.tests)} />
					<TestTotalSummary tests={TestModel.removeNilTest(formik.values.tests)} />
				</Stack>
			</Stack>
		</>
	);
};
